// Utility functions for currency detection and location-based currency mapping

// Comprehensive country to currency mapping (200+ countries and territories)
const COUNTRY_CURRENCY_MAP = {
  // North America
  US: 'USD', // United States
  CA: 'CAD', // Canada
  MX: 'MXN', // Mexico
  GT: 'GTQ', // Guatemala
  BZ: 'BZD', // Belize
  SV: 'USD', // El Salvador (uses USD)
  HN: 'HNL', // Honduras
  NI: 'NIO', // Nicaragua
  CR: 'CRC', // Costa Rica
  PA: 'PAB', // Panama
  CU: 'CUP', // Cuba
  JM: 'JMD', // Jamaica
  HT: 'HTG', // Haiti
  DO: 'DOP', // Dominican Republic
  TT: 'TTD', // Trinidad and Tobago
  BB: 'BBD', // Barbados
  BS: 'BSD', // Bahamas

  // South America
  BR: 'BRL', // Brazil
  AR: 'ARS', // Argentina
  CL: 'CLP', // Chile
  CO: 'COP', // Colombia
  PE: 'PEN', // Peru
  VE: 'VED', // Venezuela
  UY: 'UYU', // Uruguay
  PY: 'PYG', // Paraguay
  BO: 'BOB', // Bolivia
  EC: 'USD', // Ecuador (uses USD)
  GY: 'GYD', // Guyana
  SR: 'SRD', // Suriname
  GF: 'EUR', // French Guiana

  // Europe
  GB: 'GBP', // United Kingdom
  IE: 'EUR', // Ireland
  FR: 'EUR', // France
  DE: 'EUR', // Germany
  IT: 'EUR', // Italy
  ES: 'EUR', // Spain
  PT: 'EUR', // Portugal
  NL: 'EUR', // Netherlands
  BE: 'EUR', // Belgium
  LU: 'EUR', // Luxembourg
  AT: 'EUR', // Austria
  CH: 'CHF', // Switzerland
  LI: 'CHF', // Liechtenstein
  MC: 'EUR', // Monaco
  AD: 'EUR', // Andorra
  SM: 'EUR', // San Marino
  VA: 'EUR', // Vatican City
  MT: 'EUR', // Malta
  CY: 'EUR', // Cyprus
  GR: 'EUR', // Greece
  FI: 'EUR', // Finland
  EE: 'EUR', // Estonia
  LV: 'EUR', // Latvia
  LT: 'EUR', // Lithuania
  SE: 'SEK', // Sweden
  NO: 'NOK', // Norway
  DK: 'DKK', // Denmark
  IS: 'ISK', // Iceland
  PL: 'PLN', // Poland
  CZ: 'CZK', // Czech Republic
  SK: 'EUR', // Slovakia
  HU: 'HUF', // Hungary
  SI: 'EUR', // Slovenia
  HR: 'EUR', // Croatia (switched to EUR in 2023)
  BA: 'BAM', // Bosnia and Herzegovina
  RS: 'RSD', // Serbia
  ME: 'EUR', // Montenegro
  MK: 'MKD', // North Macedonia
  AL: 'ALL', // Albania
  XK: 'EUR', // Kosovo
  BG: 'BGN', // Bulgaria
  RO: 'RON', // Romania
  MD: 'MDL', // Moldova
  UA: 'UAH', // Ukraine
  BY: 'BYN', // Belarus
  RU: 'RUB', // Russia

  // Asia
  CN: 'CNY', // China
  HK: 'HKD', // Hong Kong
  MO: 'MOP', // Macau
  TW: 'TWD', // Taiwan
  JP: 'JPY', // Japan
  KR: 'KRW', // South Korea
  KP: 'KPW', // North Korea
  MN: 'MNT', // Mongolia
  KZ: 'KZT', // Kazakhstan
  KG: 'KGS', // Kyrgyzstan
  TJ: 'TJS', // Tajikistan
  UZ: 'UZS', // Uzbekistan
  TM: 'TMT', // Turkmenistan
  AF: 'AFN', // Afghanistan
  PK: 'PKR', // Pakistan
  IN: 'INR', // India
  NP: 'NPR', // Nepal
  BT: 'BTN', // Bhutan
  BD: 'BDT', // Bangladesh
  LK: 'LKR', // Sri Lanka
  MV: 'MVR', // Maldives
  MM: 'MMK', // Myanmar
  TH: 'THB', // Thailand
  LA: 'LAK', // Laos
  KH: 'KHR', // Cambodia
  VN: 'VND', // Vietnam
  MY: 'MYR', // Malaysia
  SG: 'SGD', // Singapore
  BN: 'BND', // Brunei
  ID: 'IDR', // Indonesia
  TL: 'USD', // East Timor (uses USD)
  PH: 'PHP', // Philippines

  // Middle East
  TR: 'TRY', // Turkey
  SY: 'SYP', // Syria
  LB: 'LBP', // Lebanon
  JO: 'JOD', // Jordan
  IL: 'ILS', // Israel
  PS: 'ILS', // Palestine (uses ILS and JOD)
  SA: 'SAR', // Saudi Arabia
  YE: 'YER', // Yemen
  OM: 'OMR', // Oman
  AE: 'AED', // UAE
  QA: 'QAR', // Qatar
  BH: 'BHD', // Bahrain
  KW: 'KWD', // Kuwait
  IQ: 'IQD', // Iraq
  IR: 'IRR', // Iran
  AM: 'AMD', // Armenia
  AZ: 'AZN', // Azerbaijan
  GE: 'GEL', // Georgia

  // Africa
  EG: 'EGP', // Egypt
  LY: 'LYD', // Libya
  TN: 'TND', // Tunisia
  DZ: 'DZD', // Algeria
  MA: 'MAD', // Morocco
  EH: 'MAD', // Western Sahara
  MR: 'MRU', // Mauritania
  ML: 'XOF', // Mali
  BF: 'XOF', // Burkina Faso
  NE: 'XOF', // Niger
  SN: 'XOF', // Senegal
  GM: 'GMD', // Gambia
  GW: 'XOF', // Guinea-Bissau
  GN: 'GNF', // Guinea
  SL: 'SLE', // Sierra Leone
  LR: 'LRD', // Liberia
  CI: 'XOF', // Côte d'Ivoire
  GH: 'GHS', // Ghana
  TG: 'XOF', // Togo
  BJ: 'XOF', // Benin
  NG: 'NGN', // Nigeria
  CM: 'XAF', // Cameroon
  CF: 'XAF', // Central African Republic
  TD: 'XAF', // Chad
  SD: 'SDG', // Sudan
  SS: 'SSP', // South Sudan
  ET: 'ETB', // Ethiopia
  ER: 'ERN', // Eritrea
  DJ: 'DJF', // Djibouti
  SO: 'SOS', // Somalia
  KE: 'KES', // Kenya
  UG: 'UGX', // Uganda
  RW: 'RWF', // Rwanda
  BI: 'BIF', // Burundi
  TZ: 'TZS', // Tanzania
  MZ: 'MZN', // Mozambique
  MW: 'MWK', // Malawi
  ZM: 'ZMW', // Zambia
  ZW: 'ZWL', // Zimbabwe
  BW: 'BWP', // Botswana
  NA: 'NAD', // Namibia
  ZA: 'ZAR', // South Africa
  LS: 'LSL', // Lesotho
  SZ: 'SZL', // Eswatini
  MG: 'MGA', // Madagascar
  MU: 'MUR', // Mauritius
  SC: 'SCR', // Seychelles
  KM: 'KMF', // Comoros
  CV: 'CVE', // Cape Verde
  ST: 'STN', // São Tomé and Príncipe
  GQ: 'XAF', // Equatorial Guinea
  GA: 'XAF', // Gabon
  CG: 'XAF', // Republic of the Congo
  CD: 'CDF', // Democratic Republic of the Congo
  AO: 'AOA', // Angola

  // Oceania
  AU: 'AUD', // Australia
  NZ: 'NZD', // New Zealand
  PG: 'PGK', // Papua New Guinea
  FJ: 'FJD', // Fiji
  NC: 'XPF', // New Caledonia
  VU: 'VUV', // Vanuatu
  SB: 'SBD', // Solomon Islands
  WS: 'WST', // Samoa
  TO: 'TOP', // Tonga
  KI: 'AUD', // Kiribati (uses AUD)
  TV: 'AUD', // Tuvalu (uses AUD)
  NR: 'AUD', // Nauru (uses AUD)
  PW: 'USD', // Palau (uses USD)
  FM: 'USD', // Federated States of Micronesia (uses USD)
  MH: 'USD', // Marshall Islands (uses USD)

  // Caribbean
  CW: 'ANG', // Curaçao
  AW: 'AWG', // Aruba
  BQ: 'USD', // Caribbean Netherlands (uses USD)
  SX: 'ANG', // Sint Maarten
  MF: 'EUR', // Saint Martin (French)
  GP: 'EUR', // Guadeloupe
  MQ: 'EUR', // Martinique
  PR: 'USD', // Puerto Rico (uses USD)
  VI: 'USD', // U.S. Virgin Islands (uses USD)
  VG: 'USD', // British Virgin Islands (uses USD)
  AI: 'XCD', // Anguilla
  MS: 'XCD', // Montserrat
  AG: 'XCD', // Antigua and Barbuda
  KN: 'XCD', // Saint Kitts and Nevis
  DM: 'XCD', // Dominica
  LC: 'XCD', // Saint Lucia
  VC: 'XCD', // Saint Vincent and the Grenadines
  GD: 'XCD', // Grenada
  KY: 'KYD', // Cayman Islands
  TC: 'USD', // Turks and Caicos Islands (uses USD)
  BM: 'BMD', // Bermuda

  // Dependencies and Special Cases
  FO: 'DKK', // Faroe Islands
  GL: 'DKK', // Greenland
  GI: 'GIP', // Gibraltar
  JE: 'GBP', // Jersey
  GG: 'GBP', // Guernsey
  IM: 'GBP', // Isle of Man
  FK: 'FKP', // Falkland Islands
  GS: 'GBP', // South Georgia and the South Sandwich Islands
  SH: 'SHP', // Saint Helena
  AS: 'USD', // American Samoa (uses USD)
  GU: 'USD', // Guam (uses USD)
  MP: 'USD', // Northern Mariana Islands (uses USD)
  UM: 'USD', // U.S. Minor Outlying Islands (uses USD)
  RE: 'EUR', // Réunion
  YT: 'EUR', // Mayotte
  TF: 'EUR', // French Southern Territories
  BV: 'NOK', // Bouvet Island
  SJ: 'NOK', // Svalbard and Jan Mayen
  HM: 'AUD', // Heard Island and McDonald Islands
  CX: 'AUD', // Christmas Island
  CC: 'AUD', // Cocos Islands
  NF: 'AUD', // Norfolk Island
  CK: 'NZD', // Cook Islands
  NU: 'NZD', // Niue
  TK: 'NZD', // Tokelau
  PN: 'NZD', // Pitcairn Islands
  WF: 'XPF', // Wallis and Futuna
  PF: 'XPF', // French Polynesia
  AQ: 'USD', // Antarctica (no official currency, using USD)
  IO: 'USD', // British Indian Ocean Territory (uses USD)
};

// Get currency based on country code
export const getCurrencyByCountryCode = countryCode => {
  const currency = COUNTRY_CURRENCY_MAP[countryCode?.toUpperCase()];
  return currency || 'USD'; // Default to USD if country not found
};

// Helper function to create timeout for fetch requests
const createTimeoutSignal = timeoutMs => {
  if (typeof AbortController !== 'undefined') {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), timeoutMs);
    return controller.signal;
  }
  return undefined;
};

// Get user's location using IP-based geolocation
export const getUserLocationCurrency = async () => {
  try {
    // First try to get location from IP
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      // Add timeout for production
      signal: createTimeoutSignal(10000),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (data.country_code) {
      const currency = getCurrencyByCountryCode(data.country_code);
      return {
        currency,
        country: data.country_name,
        countryCode: data.country_code,
        city: data.city,
        region: data.region,
      };
    }
  } catch (error) {
    console.warn('Error detecting location:', error);
  }

  return null;
};

// Get user's location using browser geolocation API (more accurate but requires permission)
export const getUserLocationByGeolocation = () => {
  return new Promise(resolve => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async position => {
        try {
          const { latitude, longitude } = position.coords;

          // Use reverse geocoding to get country from coordinates
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
            {
              method: 'GET',
              headers: {
                Accept: 'application/json',
              },
              // Add timeout for production
              signal: createTimeoutSignal(10000),
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();

          if (data.countryCode) {
            const currency = getCurrencyByCountryCode(data.countryCode);

            resolve({
              currency,
              country: data.countryName,
              countryCode: data.countryCode,
              city: data.city,
              region: data.principalSubdivision,
            });
          } else {
            resolve(null);
          }
        } catch (error) {
          console.warn('Error with reverse geocoding:', error);
          resolve(null);
        }
      },
      error => {
        console.warn('Geolocation error:', error);
        resolve(null);
      },
      { timeout: 10000 }
    );
  });
};

// Enhanced timezone-based currency detection as fallback
export const getCurrencyFromTimezone = () => {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Map some common timezones to countries/currencies
    const timezoneToCountry = {
      // United States
      'America/New_York': 'US',
      'America/Chicago': 'US',
      'America/Denver': 'US',
      'America/Los_Angeles': 'US',
      'America/Phoenix': 'US',
      'America/Anchorage': 'US',
      'Pacific/Honolulu': 'US',

      // United Kingdom
      'Europe/London': 'GB',

      // Europe
      'Europe/Paris': 'FR',
      'Europe/Berlin': 'DE',
      'Europe/Rome': 'IT',
      'Europe/Madrid': 'ES',
      'Europe/Amsterdam': 'NL',
      'Europe/Brussels': 'BE',
      'Europe/Vienna': 'AT',
      'Europe/Stockholm': 'SE',
      'Europe/Copenhagen': 'DK',
      'Europe/Oslo': 'NO',
      'Europe/Helsinki': 'FI',
      'Europe/Warsaw': 'PL',
      'Europe/Prague': 'CZ',
      'Europe/Budapest': 'HU',
      'Europe/Bucharest': 'RO',
      'Europe/Athens': 'GR',
      'Europe/Sofia': 'BG',
      'Europe/Zagreb': 'HR',
      'Europe/Ljubljana': 'SI',
      'Europe/Bratislava': 'SK',

      // Asia
      'Asia/Tokyo': 'JP',
      'Asia/Seoul': 'KR',
      'Asia/Shanghai': 'CN',
      'Asia/Hong_Kong': 'HK',
      'Asia/Singapore': 'SG',
      'Asia/Bangkok': 'TH',
      'Asia/Jakarta': 'ID',
      'Asia/Manila': 'PH',
      'Asia/Kuala_Lumpur': 'MY',
      'Asia/Dhaka': 'BD',
      'Asia/Kolkata': 'IN',
      'Asia/Karachi': 'PK',
      'Asia/Dubai': 'AE',
      'Asia/Riyadh': 'SA',
      'Asia/Tehran': 'IR',
      'Asia/Istanbul': 'TR',

      // Australia & Oceania
      'Australia/Sydney': 'AU',
      'Australia/Melbourne': 'AU',
      'Australia/Brisbane': 'AU',
      'Australia/Perth': 'AU',
      'Pacific/Auckland': 'NZ',

      // Americas
      'America/Toronto': 'CA',
      'America/Vancouver': 'CA',
      'America/Sao_Paulo': 'BR',
      'America/Mexico_City': 'MX',
      'America/Argentina/Buenos_Aires': 'AR',
      'America/Santiago': 'CL',
      'America/Lima': 'PE',
      'America/Bogota': 'CO',

      // Africa
      'Africa/Cairo': 'EG',
      'Africa/Lagos': 'NG',
      'Africa/Johannesburg': 'ZA',
      'Africa/Nairobi': 'KE',
      'Africa/Casablanca': 'MA',
    };

    const countryCode = timezoneToCountry[timezone];
    if (countryCode) {
      const currency = getCurrencyByCountryCode(countryCode);

      return {
        currency,
        countryCode,
        country: 'Unknown', // We don't have full country name from timezone
        method: 'timezone',
        isDefault: false,
      };
    }
  } catch (error) {
    console.warn('Error detecting timezone:', error);
  }

  return null;
};

// Main function to get user's currency - tries multiple methods with enhanced fallbacks
export const detectUserCurrency = async () => {
  // Check if currency detection is enabled
  const enableCurrencyDetection = process.env.REACT_APP_ENABLE_CURRENCY_DETECTION !== 'false';
  const enableBrowserGeolocation = process.env.REACT_APP_ENABLE_BROWSER_GEOLOCATION !== 'false';
  const enableIpGeolocation = process.env.REACT_APP_ENABLE_IP_GEOLOCATION !== 'false';
  const enableTimezoneDetection = process.env.REACT_APP_ENABLE_TIMEZONE_DETECTION !== 'false';

  if (!enableCurrencyDetection) {
    return {
      currency: 'USD',
      country: 'Unknown',
      countryCode: 'US',
      isDefault: true,
      method: 'env-disabled',
    };
  }

  let locationData = null;

  // Method 1: Try geolocation first (most accurate)
  if (enableBrowserGeolocation) {
    locationData = await getUserLocationByGeolocation();
  }

  // Method 2: If geolocation fails, try IP-based detection
  if (!locationData && enableIpGeolocation) {
    locationData = await getUserLocationCurrency();
  }

  // Method 3: If both fail, try timezone-based detection
  if (!locationData && enableTimezoneDetection) {
    locationData = getCurrencyFromTimezone();
  }

  // Method 4: If all fail, return default
  if (!locationData) {
    return {
      currency: 'USD',
      country: 'Unknown',
      countryCode: 'US',
      isDefault: true,
      method: 'default',
    };
  }

  return locationData;
};

// Currency display names and symbols (comprehensive list)
export const CURRENCY_DISPLAY = {
  // Major Currencies
  USD: { symbol: '$', name: 'US Dollar' },
  EUR: { symbol: '€', name: 'Euro' },
  GBP: { symbol: '£', name: 'British Pound' },
  JPY: { symbol: '¥', name: 'Japanese Yen' },
  CHF: { symbol: 'CHF', name: 'Swiss Franc' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar' },
  AUD: { symbol: 'A$', name: 'Australian Dollar' },
  NZD: { symbol: 'NZ$', name: 'New Zealand Dollar' },

  // Asia-Pacific
  CNY: { symbol: '¥', name: 'Chinese Yuan' },
  HKD: { symbol: 'HK$', name: 'Hong Kong Dollar' },
  SGD: { symbol: 'S$', name: 'Singapore Dollar' },
  KRW: { symbol: '₩', name: 'South Korean Won' },
  INR: { symbol: '₹', name: 'Indian Rupee' },
  THB: { symbol: '฿', name: 'Thai Baht' },
  MYR: { symbol: 'RM', name: 'Malaysian Ringgit' },
  IDR: { symbol: 'Rp', name: 'Indonesian Rupiah' },
  PHP: { symbol: '₱', name: 'Philippine Peso' },
  VND: { symbol: '₫', name: 'Vietnamese Dong' },
  TWD: { symbol: 'NT$', name: 'Taiwan Dollar' },
  BDT: { symbol: '৳', name: 'Bangladeshi Taka' },
  PKR: { symbol: '₨', name: 'Pakistani Rupee' },
  LKR: { symbol: '₨', name: 'Sri Lankan Rupee' },
  NPR: { symbol: '₨', name: 'Nepalese Rupee' },
  LAK: { symbol: '₭', name: 'Lao Kip' },
  KHR: { symbol: '៛', name: 'Cambodian Riel' },
  MMK: { symbol: 'K', name: 'Myanmar Kyat' },
  BND: { symbol: 'B$', name: 'Brunei Dollar' },
  MOP: { symbol: 'MOP$', name: 'Macanese Pataca' },
  KPW: { symbol: '₩', name: 'North Korean Won' },
  MNT: { symbol: '₮', name: 'Mongolian Tugrik' },
  MVR: { symbol: 'Rf', name: 'Maldivian Rufiyaa' },
  BTN: { symbol: 'Nu.', name: 'Bhutanese Ngultrum' },
  AFN: { symbol: '؋', name: 'Afghan Afghani' },

  // Central Asia
  KZT: { symbol: '₸', name: 'Kazakhstani Tenge' },
  KGS: { symbol: 'сом', name: 'Kyrgyzstani Som' },
  TJS: { symbol: 'ЅМ', name: 'Tajikistani Somoni' },
  UZS: { symbol: 'soʻm', name: 'Uzbekistani Som' },
  TMT: { symbol: 'T', name: 'Turkmenistani Manat' },

  // Middle East
  AED: { symbol: 'د.إ', name: 'UAE Dirham' },
  SAR: { symbol: '﷼', name: 'Saudi Riyal' },
  QAR: { symbol: 'ر.ق', name: 'Qatari Riyal' },
  KWD: { symbol: 'د.ك', name: 'Kuwaiti Dinar' },
  BHD: { symbol: '.د.ب', name: 'Bahraini Dinar' },
  OMR: { symbol: 'ر.ع.', name: 'Omani Rial' },
  ILS: { symbol: '₪', name: 'Israeli Shekel' },
  JOD: { symbol: 'د.ا', name: 'Jordanian Dinar' },
  LBP: { symbol: 'ل.ل', name: 'Lebanese Pound' },
  SYP: { symbol: '£', name: 'Syrian Pound' },
  IQD: { symbol: 'ع.د', name: 'Iraqi Dinar' },
  IRR: { symbol: '﷼', name: 'Iranian Rial' },
  TRY: { symbol: '₺', name: 'Turkish Lira' },
  YER: { symbol: '﷼', name: 'Yemeni Rial' },
  AMD: { symbol: '֏', name: 'Armenian Dram' },
  AZN: { symbol: '₼', name: 'Azerbaijani Manat' },
  GEL: { symbol: '₾', name: 'Georgian Lari' },

  // Europe (Non-Euro)
  SEK: { symbol: 'kr', name: 'Swedish Krona' },
  NOK: { symbol: 'kr', name: 'Norwegian Krone' },
  DKK: { symbol: 'kr', name: 'Danish Krone' },
  ISK: { symbol: 'kr', name: 'Icelandic Króna' },
  PLN: { symbol: 'zł', name: 'Polish Zloty' },
  CZK: { symbol: 'Kč', name: 'Czech Koruna' },
  HUF: { symbol: 'Ft', name: 'Hungarian Forint' },
  RON: { symbol: 'lei', name: 'Romanian Leu' },
  BGN: { symbol: 'лв', name: 'Bulgarian Lev' },
  RSD: { symbol: 'дин', name: 'Serbian Dinar' },
  BAM: { symbol: 'КМ', name: 'Bosnia-Herzegovina Convertible Mark' },
  MKD: { symbol: 'ден', name: 'Macedonian Denar' },
  ALL: { symbol: 'L', name: 'Albanian Lek' },
  RUB: { symbol: '₽', name: 'Russian Ruble' },
  UAH: { symbol: '₴', name: 'Ukrainian Hryvnia' },
  BYN: { symbol: 'Br', name: 'Belarusian Ruble' },
  MDL: { symbol: 'L', name: 'Moldovan Leu' },
  GIP: { symbol: '£', name: 'Gibraltar Pound' },
  FKP: { symbol: '£', name: 'Falkland Islands Pound' },
  SHP: { symbol: '£', name: 'Saint Helena Pound' },

  // Africa
  ZAR: { symbol: 'R', name: 'South African Rand' },
  NGN: { symbol: '₦', name: 'Nigerian Naira' },
  EGP: { symbol: 'ج.م', name: 'Egyptian Pound' },
  MAD: { symbol: 'د.م.', name: 'Moroccan Dirham' },
  KES: { symbol: 'KSh', name: 'Kenyan Shilling' },
  GHS: { symbol: 'GH₵', name: 'Ghanaian Cedi' },
  ETB: { symbol: 'Br', name: 'Ethiopian Birr' },
  TZS: { symbol: 'TSh', name: 'Tanzanian Shilling' },
  UGX: { symbol: 'USh', name: 'Ugandan Shilling' },
  ZMW: { symbol: 'ZK', name: 'Zambian Kwacha' },
  BWP: { symbol: 'P', name: 'Botswanan Pula' },
  MUR: { symbol: '₨', name: 'Mauritian Rupee' },
  NAD: { symbol: '$', name: 'Namibian Dollar' },
  MZN: { symbol: 'MT', name: 'Mozambican Metical' },
  AOA: { symbol: 'Kz', name: 'Angolan Kwanza' },
  DZD: { symbol: 'د.ج', name: 'Algerian Dinar' },
  TND: { symbol: 'د.ت', name: 'Tunisian Dinar' },
  LYD: { symbol: 'ل.د', name: 'Libyan Dinar' },
  XOF: { symbol: 'CFA', name: 'West African CFA Franc' },
  XAF: { symbol: 'FCFA', name: 'Central African CFA Franc' },
  CDF: { symbol: 'FC', name: 'Congolese Franc' },
  RWF: { symbol: 'RF', name: 'Rwandan Franc' },
  BIF: { symbol: 'FBu', name: 'Burundian Franc' },
  DJF: { symbol: 'Fdj', name: 'Djiboutian Franc' },
  ERN: { symbol: 'Nfk', name: 'Eritrean Nakfa' },
  SOS: { symbol: 'S', name: 'Somali Shilling' },
  SDG: { symbol: 'ج.س.', name: 'Sudanese Pound' },
  SSP: { symbol: '£', name: 'South Sudanese Pound' },
  MWK: { symbol: 'MK', name: 'Malawian Kwacha' },
  ZWL: { symbol: 'Z$', name: 'Zimbabwean Dollar' },
  LSL: { symbol: 'L', name: 'Lesotho Loti' },
  SZL: { symbol: 'L', name: 'Swazi Lilangeni' },
  MGA: { symbol: 'Ar', name: 'Malagasy Ariary' },
  SCR: { symbol: '₨', name: 'Seychellois Rupee' },
  KMF: { symbol: 'CF', name: 'Comorian Franc' },
  CVE: { symbol: '$', name: 'Cape Verdean Escudo' },
  STN: { symbol: 'Db', name: 'São Tomé and Príncipe Dobra' },
  GMD: { symbol: 'D', name: 'Gambian Dalasi' },
  GNF: { symbol: 'FG', name: 'Guinean Franc' },
  SLE: { symbol: 'Le', name: 'Sierra Leonean Leone' },
  LRD: { symbol: '$', name: 'Liberian Dollar' },
  MRU: { symbol: 'UM', name: 'Mauritanian Ouguiya' },

  // Americas
  BRL: { symbol: 'R$', name: 'Brazilian Real' },
  MXN: { symbol: '$', name: 'Mexican Peso' },
  ARS: { symbol: '$', name: 'Argentine Peso' },
  CLP: { symbol: '$', name: 'Chilean Peso' },
  COP: { symbol: '$', name: 'Colombian Peso' },
  PEN: { symbol: 'S/', name: 'Peruvian Sol' },
  UYU: { symbol: '$U', name: 'Uruguayan Peso' },
  PYG: { symbol: 'Gs', name: 'Paraguayan Guarani' },
  BOB: { symbol: '$b', name: 'Bolivian Boliviano' },
  VED: { symbol: 'Bs.S', name: 'Venezuelan Bolívar Soberano' },
  GYD: { symbol: '$', name: 'Guyanese Dollar' },
  SRD: { symbol: '$', name: 'Surinamese Dollar' },
  GTQ: { symbol: 'Q', name: 'Guatemalan Quetzal' },
  BZD: { symbol: 'BZ$', name: 'Belize Dollar' },
  HNL: { symbol: 'L', name: 'Honduran Lempira' },
  NIO: { symbol: 'C$', name: 'Nicaraguan Córdoba' },
  CRC: { symbol: '₡', name: 'Costa Rican Colón' },
  PAB: { symbol: 'B/.', name: 'Panamanian Balboa' },
  CUP: { symbol: '₱', name: 'Cuban Peso' },
  JMD: { symbol: 'J$', name: 'Jamaican Dollar' },
  HTG: { symbol: 'G', name: 'Haitian Gourde' },
  DOP: { symbol: 'RD$', name: 'Dominican Peso' },
  TTD: { symbol: 'TT$', name: 'Trinidad and Tobago Dollar' },
  BBD: { symbol: 'Bds$', name: 'Barbadian Dollar' },
  BSD: { symbol: 'B$', name: 'Bahamian Dollar' },
  XCD: { symbol: 'EC$', name: 'East Caribbean Dollar' },
  KYD: { symbol: 'CI$', name: 'Cayman Islands Dollar' },
  BMD: { symbol: 'BD$', name: 'Bermudian Dollar' },
  ANG: { symbol: 'ƒ', name: 'Netherlands Antillean Guilder' },
  AWG: { symbol: 'ƒ', name: 'Aruban Florin' },

  // Oceania
  PGK: { symbol: 'K', name: 'Papua New Guinean Kina' },
  FJD: { symbol: 'FJ$', name: 'Fijian Dollar' },
  XPF: { symbol: '₣', name: 'CFP Franc' },
  VUV: { symbol: 'VT', name: 'Vanuatu Vatu' },
  SBD: { symbol: 'SI$', name: 'Solomon Islands Dollar' },
  WST: { symbol: 'WS$', name: 'Samoan Tala' },
  TOP: { symbol: 'T$', name: 'Tongan Paʻanga' },

  // Special and Regional Currencies
  XDR: { symbol: 'SDR', name: 'Special Drawing Rights' },
  XAG: { symbol: 'oz', name: 'Silver Ounce' },
  XAU: { symbol: 'oz', name: 'Gold Ounce' },
  XPD: { symbol: 'oz', name: 'Palladium Ounce' },
  XPT: { symbol: 'oz', name: 'Platinum Ounce' },

  // Cryptocurrencies (if supported)
  BTC: { symbol: '₿', name: 'Bitcoin' },
  ETH: { symbol: 'Ξ', name: 'Ethereum' },
};

// Get currency symbol and name
export const getCurrencyInfo = currencyCode => {
  return CURRENCY_DISPLAY[currencyCode] || { symbol: currencyCode, name: currencyCode };
};

// Enhanced formatMoney function that supports all currencies
export const formatMoneyWithCurrency = (intl, price, options = {}) => {
  if (!price || typeof price.amount !== 'number' || !price.currency) {
    return '';
  }

  const { showSymbol = true, showCode = false } = options;
  const currencyInfo = getCurrencyInfo(price.currency);

  try {
    // Try to use the built-in Intl.NumberFormat for currency formatting
    const formatter = new Intl.NumberFormat(intl.locale, {
      style: 'currency',
      currency: price.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    // Convert subunits to main units (divide by 100 for most currencies)
    // Note: Some currencies like JPY don't use subunits
    const divisor = getCurrencyDivisor(price.currency);
    const amount = price.amount / divisor;

    const formatted = formatter.format(amount);

    return formatted;
  } catch (error) {
    console.warn('Currency formatting fallback for:', price.currency, error);

    // Fallback formatting for unsupported currencies
    const divisor = getCurrencyDivisor(price.currency);
    const amount = price.amount / divisor;
    const formattedAmount = amount.toLocaleString(intl.locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    if (showSymbol) {
      return `${currencyInfo.symbol}${formattedAmount}`;
    } else if (showCode) {
      return `${formattedAmount} ${price.currency}`;
    } else {
      return `${currencyInfo.symbol}${formattedAmount}`;
    }
  }
};

// Get currency divisor (most currencies use 100, some use 1)
export const getCurrencyDivisor = currencyCode => {
  // Currencies that don't use subunits (no decimal places)
  const noSubunitCurrencies = [
    'JPY', // Japanese Yen
    'KRW', // South Korean Won
    'VND', // Vietnamese Dong
    'CLP', // Chilean Peso
    'ISK', // Icelandic Króna
    'HUF', // Hungarian Forint
    'TWD', // Taiwan Dollar
    'KMF', // Comorian Franc
    'XPF', // CFP Franc
    'BIF', // Burundian Franc
    'DJF', // Djiboutian Franc
    'GNF', // Guinean Franc
    'PYG', // Paraguayan Guarani
    'RWF', // Rwandan Franc
    'UGX', // Ugandan Shilling
    'VUV', // Vanuatu Vatu
    'XAF', // Central African CFA Franc
    'XOF', // West African CFA Franc
    'KPW', // North Korean Won
    'LAK', // Lao Kip
    'SOS', // Somali Shilling
    'UZS', // Uzbekistani Som
    'IRR', // Iranian Rial
    'SLE', // Sierra Leonean Leone
    'TZS', // Tanzanian Shilling
    'CDF', // Congolese Franc
    'KHR', // Cambodian Riel
    'MMK', // Myanmar Kyat
    'STN', // São Tomé and Príncipe Dobra
    'ZWL', // Zimbabwean Dollar
  ];

  // Currencies that use 1000 as divisor
  const thousandDivisorCurrencies = [
    'BHD', // Bahraini Dinar
    'JOD', // Jordanian Dinar
    'KWD', // Kuwaiti Dinar
    'OMR', // Omani Rial
    'TND', // Tunisian Dinar
  ];

  if (noSubunitCurrencies.includes(currencyCode)) {
    return 1;
  } else if (thousandDivisorCurrencies.includes(currencyCode)) {
    return 1000;
  } else {
    return 100; // Most currencies use 100 subunits
  }
};

// Enhanced price data function that formats any currency properly
export const formatPriceData = (price, intl, options = {}) => {
  if (!price) {
    return { formattedPrice: '', priceTitle: '' };
  }

  try {
    const formattedPrice = formatMoneyWithCurrency(intl, price, options);
    return {
      formattedPrice,
      priceTitle: formattedPrice,
    };
  } catch (error) {
    console.error('Error formatting price:', error);
    const currencyInfo = getCurrencyInfo(price.currency);
    return {
      formattedPrice: `${currencyInfo.symbol}${price.amount / getCurrencyDivisor(price.currency)}`,
      priceTitle: `${price.amount / getCurrencyDivisor(price.currency)} ${currencyInfo.name}`,
    };
  }
};

// Check if a currency is supported by the browser's Intl.NumberFormat
export const isCurrencySupported = currencyCode => {
  try {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    });
    formatter.format(100);
    return true;
  } catch (error) {
    return false;
  }
};
